import { CorePermissions } from "app/appPermissions";
import { GenericAbortSignal } from "axios";
import { MprcConfirmDialog } from "components";
import { AutoCompleteDataModel, AutoCompleteSuggestions, MprcAutoComplete } from "components/MprcAutoComplete";
import { IAutoCompleteProps } from "components/MprcAutoComplete/AutoComplete";
import { createAutoCompleteModel } from "components/MprcAutoComplete/AutoCompleteDataModel";
import { TagAutoCompleteDataModelDto, TagUsage } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import { isCanceledError, useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";

////////////////////////////////////////////////////////////////////////////

interface IProps {
    usage: TagUsage;
    label?: string | null;
    placeholder?: string;
    canCreate?: boolean;
    exceptTagIds?: string[];
}

type Props = IProps & Omit<IAutoCompleteProps, 'onGetSuggestions' | 'label' | 'placeholder'>;

////////////////////////////////////////////////////////////////////////////

/**
 * Auto complete de tags para os cadastros.
 * @param props
 */
export const TagAutoComplete: React.FC<Props> = (props) => {

    const { usage, label, placeholder, exceptTagIds, canCreate = false, onError } = props;

    const api = useCoreApi();
    const { t } = useTranslation();
    const { permissionExplorer } = useSession();
    const { enqueueSnackbar } = useSnackbar();
    const [dialog, setDialog] = useState<any>();

    const canAddNew = canCreate && permissionExplorer.hasOne([CorePermissions.TagsManage]);

    ///
    const getSuggestions = (searchText: string, pageNumber: number, pageSize: number, trySelectedHistory: boolean, signal: GenericAbortSignal | undefined): Promise<AutoCompleteSuggestions<AutoCompleteDataModel>> => {
        searchText = searchText.toLowerCase();

        return new Promise<AutoCompleteSuggestions<AutoCompleteDataModel>>((resolve) => {
            api.v1.suggestions.tags({
                name: searchText,
                usage: usage,
                exceptTagIds: exceptTagIds ?? [],
                pageNumber: pageNumber,
                pageSize: pageSize,
            }, { signal })
                .then(result => resolve({
                    items: result.data.items as AutoCompleteDataModel[],
                    totalRows: result.data.totalRows
                }))
                .catch(e => {
                    if (!isCanceledError(e)) {
                        resolve({ items: [], totalRows: 0 });
                        if (onError) onError(e);
                    }
                });
        });
    };

    // Solicita confirmação ao incluir uma nova tag.
    const handleKeyReturn = (value: string) => {
        if (value.length > 255) {
            enqueueSnackbar(t("Messages.MaxStringLimit", { ns: "shared", count: 255 }), { variant: "error" });
            return;
        }
        if (value.length > 0 && canAddNew) {
            setDialog(
                <MprcConfirmDialog
                    open={true}
                    message={t("Tag.TagsAutoCompleteBox.ConfirmAddMessage", { name: value })}
                    onClose={(confirm) => { if (confirm) handleNewTag(value); else setDialog(null); }}
                />
            );
        }
    };

    // Inclui uma nova tag na lista e salva.
    const handleNewTag = (tag: string) => {
        var item = createAutoCompleteModel(v4(), tag);

        if (canAddNew) props.onChange([...props.selectedItems, item] as TagAutoCompleteDataModelDto[]);
        setDialog(null);
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            {dialog}
            <MprcAutoComplete
                {...props}
                label={label == null ? "" : label ?? t("Tag.TagsAutoComplete.TagsLabel")}
                placeholder={t(canAddNew ? "Tag.TagsAutoComplete.SelectAndNewPlaceholder" : placeholder ?? "Tag.TagsAutoComplete.SelectPlaceholder")}
                onGetSuggestions={getSuggestions}
                onKeyReturn={canAddNew ? handleKeyReturn : undefined}
            />
        </>
    );
};
